import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Calendar } from 'lucide-react'

interface PeriodPresetsProps {
  value: { desde: string; hasta: string } | null
  onChange: (period: { desde: string; hasta: string } | null) => void
}

type PresetKey = 'todo' | 'este-mes' | 'mes-anterior' | 'trimestre' | 'anio' | 'personalizado'

const getPresetDates = (preset: PresetKey): { desde: string; hasta: string } | null => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  switch (preset) {
    case 'todo':
      return null
    case 'este-mes': {
      const desde = new Date(year, month, 1)
      const hasta = new Date(year, month + 1, 0)
      return { desde: formatISO(desde), hasta: formatISO(hasta) }
    }
    case 'mes-anterior': {
      const desde = new Date(year, month - 1, 1)
      const hasta = new Date(year, month, 0)
      return { desde: formatISO(desde), hasta: formatISO(hasta) }
    }
    case 'trimestre': {
      const desde = new Date(year, month - 2, 1)
      const hasta = new Date(year, month + 1, 0)
      return { desde: formatISO(desde), hasta: formatISO(hasta) }
    }
    case 'anio': {
      const desde = new Date(year, 0, 1)
      const hasta = new Date(year, 11, 31)
      return { desde: formatISO(desde), hasta: formatISO(hasta) }
    }
    default:
      return null
  }
}

const formatISO = (date: Date): string => {
  return date.toISOString().split('T')[0]
}

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'todo', label: 'Todo' },
  { key: 'este-mes', label: 'Este mes' },
  { key: 'mes-anterior', label: 'Mes anterior' },
  { key: 'trimestre', label: 'Trimestre' },
  { key: 'anio', label: 'Año' },
  { key: 'personalizado', label: 'Personalizado' },
]

export function PeriodPresets({ value, onChange }: PeriodPresetsProps) {
  const [activePreset, setActivePreset] = useState<PresetKey>('todo')
  const [customDesde, setCustomDesde] = useState('')
  const [customHasta, setCustomHasta] = useState('')
  const [popoverOpen, setPopoverOpen] = useState(false)

  const handlePresetClick = (preset: PresetKey) => {
    if (preset === 'personalizado') {
      setActivePreset('personalizado')
      return
    }
    setActivePreset(preset)
    onChange(getPresetDates(preset))
  }

  const handleCustomApply = () => {
    if (customDesde && customHasta && customDesde <= customHasta) {
      setActivePreset('personalizado')
      onChange({ desde: customDesde, hasta: customHasta })
      setPopoverOpen(false)
    }
  }

  const customRangeInvalid = !!(customDesde && customHasta && customDesde > customHasta)

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {PRESETS.map((preset) => {
        if (preset.key === 'personalizado') {
          return (
            <Popover key={preset.key} open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={activePreset === 'personalizado' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handlePresetClick('personalizado')}
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  {activePreset === 'personalizado' && value
                    ? `${value.desde} - ${value.hasta}`
                    : preset.label}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-3" align="start">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Desde</Label>
                    <Input
                      type="date"
                      value={customDesde}
                      onChange={(e) => setCustomDesde(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hasta</Label>
                    <Input
                      type="date"
                      value={customHasta}
                      onChange={(e) => setCustomHasta(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  {customRangeInvalid && (
                    <p className="text-destructive text-xs">Fecha desde debe ser anterior a fecha hasta</p>
                  )}
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs"
                    onClick={handleCustomApply}
                    disabled={!customDesde || !customHasta || customRangeInvalid}
                  >
                    Aplicar
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )
        }

        return (
          <Button
            key={preset.key}
            variant={activePreset === preset.key ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => handlePresetClick(preset.key)}
          >
            {preset.label}
          </Button>
        )
      })}
    </div>
  )
}
